/**
 * Shown when the signer already has a signature (or initials) on file: preview
 * the saved image and offer to reuse it. Rendered through the app-wide
 * `AppModal` shell at the signer card's 580px width, keeping the signer
 * surface's accent bar and split footer. "Edit" hands off to the signature
 * pad's Type / Draw / Upload tabs.
 */
import { Box, ButtonBase, Link, Typography } from "@mui/material";
import { AppModal } from "global/components/AppModal";
import strings from "global/constants/StringConstants";
import urls from "global/constants/UrlConstants";
import { signerAccentBar } from "theme/colors";
import tokens from "../SignerExperience.tokens";
import { PencilIcon } from "./SignerExperienceIcons";

const { palette, modal, savedSignature } = tokens;

interface SavedSignatureDialogProps {
  open: boolean;
  /** Data URL of the signature/initials already stored for this signer. */
  image?: string;
  isInitialsPad?: boolean;
  /** False while the field is read-only — every action is inert. */
  enabled: boolean;
  onClose: () => void;
  /** Reuse the saved image on this field. */
  onAccept: () => void;
  /** Discard the saved image and open the signature pad instead. */
  onEdit: () => void;
}

const SavedSignatureDialog = ({
  open,
  image,
  isInitialsPad,
  enabled,
  onClose,
  onAccept,
  onEdit,
}: SavedSignatureDialogProps) => {
  const noun = isInitialsPad ? "initials" : "signature";

  return (
    // The signer surface keeps its own accent bar, width and button fills; the
    // shell, scrim, header, footer bar and responsive behaviour come from the
    // app-wide Modal System.
    <AppModal
      open={open}
      onClose={onClose}
      title={`Use your saved ${noun}`}
      subtitle="We already have this on file. Use it, or edit to draw, type or upload a new one."
      hideIcon
      width={modal.width}
      topSlot={
        <Box
          aria-hidden
          sx={{ height: modal.accentBarHeight, background: signerAccentBar }}
        />
      }
      footer={
        <>
          <ButtonBase
            onClick={onEdit}
            disabled={!enabled}
            sx={{
              flex: "none",
              height: modal.buttonHeight,
              px: modal.secondaryPaddingX,
              gap: "8px",
              borderRadius: modal.buttonRadius,
              border: `${modal.secondaryBorderWidth} solid ${palette.border}`,
              backgroundColor: palette.white,
              color: palette.body,
              fontFamily: "inherit",
              fontSize: modal.buttonFontSize,
              fontWeight: modal.buttonFontWeight,
              "&:hover": { backgroundColor: palette.hover },
              "&.Mui-disabled": { opacity: 0.5 },
              "&.Mui-focusVisible": {
                outline: `2px solid ${palette.accent}`,
                outlineOffset: "2px",
              },
            }}
          >
            <PencilIcon size={14} />
            Edit
          </ButtonBase>
          <ButtonBase
            onClick={onAccept}
            disabled={!enabled}
            sx={{
              flex: 1,
              height: modal.buttonHeight,
              borderRadius: modal.buttonRadius,
              // Same brand red outside an embed; follows the partner's colour inside one.
              backgroundColor: (t) => t.palette.signer.orange.main,
              color: palette.white,
              fontFamily: "inherit",
              fontSize: modal.buttonFontSize,
              fontWeight: modal.buttonFontWeight,
              "&:hover": { backgroundColor: (t) => t.palette.signer.orange.dark },
              "&.Mui-disabled": { backgroundColor: palette.inactive },
              "&.Mui-focusVisible": {
                outline: `2px solid ${palette.ink}`,
                outlineOffset: "2px",
              },
            }}
          >
            Use this {noun}
          </ButtonBase>
        </>
      }
    >
      <Box>
        <Box
          sx={{
            height: savedSignature.previewHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: savedSignature.previewPaddingX,
            borderRadius: savedSignature.previewRadius,
            border: `1px solid ${palette.border}`,
            backgroundColor: savedSignature.previewBg,
          }}
        >
          <Box
            component="img"
            src={image}
            alt={isInitialsPad ? "Saved initials" : "Saved signature"}
            sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
          />
        </Box>

        <Typography
          sx={{
            mt: savedSignature.legalMarginTop,
            fontSize: savedSignature.legalFontSize,
            lineHeight: savedSignature.legalLineHeight,
            color: palette.muted,
            textAlign: "center",
          }}
        >
          {strings.PRIVACY_POLICY}{" "}
          <Link
            target="_blank"
            href={urls.tNcUrl}
            sx={{ color: palette.accent, fontWeight: 600 }}
          >
            Terms of use
          </Link>{" "}
          ·{" "}
          <Link
            target="_blank"
            href={urls.privacyUrl}
            sx={{ color: palette.accent, fontWeight: 600 }}
          >
            Privacy policy
          </Link>
        </Typography>
      </Box>
    </AppModal>
  );
};

export default SavedSignatureDialog;
